// Pure helper for the CombatPreviewPanel — finds enemy combat units in
// the 6 hex neighbours of a given attacker unit. Heroes are excluded
// because hero-vs-hero is handled by HeroAbilityBar, not the unit combat
// predictor. Adjacency is axial hex neighbours ([+1,0], [+1,-1], [0,-1],
// [-1,0], [-1,+1], [0,+1]).

import type { GameSnapshot, SceneNode } from '@aod/protocol';

const HEX_NEIGHBOURS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export interface FindAdjacentEnemyUnitsArgs {
  readonly attacker: SceneNode;
  readonly snapshot: GameSnapshot;
  readonly localPlayerIndex: number;
}

export function findAdjacentEnemyUnits(args: FindAdjacentEnemyUnitsArgs): SceneNode[] {
  const { attacker, snapshot, localPlayerIndex } = args;
  const aq = attacker.props?.q as number | undefined;
  const ar = attacker.props?.r as number | undefined;
  if (aq === undefined || ar === undefined) return [];

  const neighbourSet = new Set(HEX_NEIGHBOURS.map(([dq, dr]) => `${aq + dq},${ar + dr}`));
  const all: ReadonlyArray<SceneNode> = snapshot.scene.children ?? [];

  return all.filter((n) => {
    if (n.type !== 'unit') return false;
    if ((n.tags as string[] | undefined)?.includes('hero')) return false;
    const ownerId = n.props?.ownerId as number | undefined;
    if (ownerId === undefined || ownerId === localPlayerIndex) return false;
    const nq = n.props?.q as number | undefined;
    const nr = n.props?.r as number | undefined;
    if (nq === undefined || nr === undefined) return false;
    return neighbourSet.has(`${nq},${nr}`);
  });
}
